<template>
  <div class="stripe-modal">
    <div class="stripe-modal__header">
      <SatoshiEyeLogo class="stripe-modal__header-logo" />
      <button
        class="stripe-modal__close-button"
        @click="handleClose"
      >
        <gl-icon
          gray
          :height="16"
          name="I-Close"
          :width="16"
        />
      </button>
    </div>

    <div
      v-if="loadingSecret"
      class="stripe-modal__loading"
    >
      <gl-loader progress-spinner />
    </div>

    <div
      v-else-if="success"
      class="stripe-modal__success"
    >
      <h2 class="stripe-modal__success-title">
        {{ isRefreshAction ? 'Quota Refreshed!' : 'Payment Successful!' }}
      </h2>
      <p class="stripe-modal__success-description">
        {{ isRefreshAction ? 'Your report limits have been successfully reset.' : 'Thank you for your purchase.' }}
      </p>

      <gl-button
        class="stripe-modal__continue-button"
        dark
        title="Continue"
        @click="handleContinueFlow"
      />
    </div>

    <div
      v-else
      class="stripe-modal__content"
    >
      <div class="stripe-modal__scroll-content">
        <div class="stripe-modal__card-wrapper">
          <PlanCard
            v-bind="packageData"
            current
            hide-action
          />
        </div>

        <form
          class="stripe-modal__form"
          @submit.prevent="handleSubmit"
        >
          <div
            v-if="isTrial"
            class="stripe-modal__trial-notice"
          >
            Your card will not be charged today. You will be billed {{ packageData.totalPrice || '0.00' }} after 7 days.
          </div>

          <div
            ref="paymentElement"
            class="stripe-modal__payment-element"
          />
        </form>
      </div>

      <div class="stripe-modal__footer">
        <button
          class="stripe-modal__pay-button"
          :class="{ 'stripe-modal__pay-button--loading': submitLoading }"
          :disabled="submitLoading || !isFormComplete"
          type="submit"
          @click="handleSubmit"
        >
          <span
            v-if="!submitLoading"
            class="stripe-modal__pay-button-content"
          >
            {{ isTrial ? 'Start 7-day Free Trial' : `Pay ${packageData.totalPrice || '0.00'}` }}
          </span>
          <span
            v-else
            class="stripe-modal__pay-button-loading"
          >
            <div class="stripe-modal__pay-button-spinner" />
            Processing...
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// components
import GlIcon from '@/components/gl-icon'
import GlButton from '@/components/gl-button'
import GlLoader from '@/components/gl-loader'
import SatoshiEyeLogo from '@/assets/svg/header/SatoshiEyeLogo.svg?inline'
import PlanCard from "@/pages/profile/components/PlanCard.vue"

// stripe
import { loadStripe } from '@stripe/stripe-js'
// Vuex
import { mapActions, mapGetters, mapMutations } from 'vuex'
// Utils
import appConfig from '@/utils/appConfig'

export default {
  name: 'StripeModal',
  components: {
    GlIcon,
    GlButton,
    GlLoader,
    SatoshiEyeLogo,
    PlanCard
  },
  props: {
    packageData: { type: Object, default: () => ({}) },
    insertClientSecret: { type: String, default: '' },
    isTrial: { type: Boolean, default: false },
    isRefreshAction: { type: Boolean, default: false }
  },
  data() {
    return {
      publishableKey: '',
      clientSecret: null,
      stripe: null,
      elements: null,
      paymentElement: null,
      success: false,
      submitLoading: false,
      loadingSecret: false,
      isFormComplete: false,
    }
  },
  computed: {
    ...mapGetters('user', ['paymentCurrency']),
    appConfig() {
      return appConfig
    },
  },
  async mounted() {
    this.publishableKey = this.appConfig.VUE_APP_STRIPE_PUBLISHABLE_KEY

    if (this.insertClientSecret) {
      this.clientSecret = this.insertClientSecret
    } else {
      await this.fetchClientSecret()
    }

    this.stripe = await loadStripe(this.publishableKey)

    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#194BFB',
        colorBackground: '#ffffff',
        colorText: '#0E0B3D',
        colorDanger: '#FF4754',
        fontFamily: 'Montserrat, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
      rules: {
        '.Input': {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #d1d5db',
        },
        '.Input:focus': {
          border: '1px solid #194BFB',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(25, 75, 251, 0.1)',
        },
      },
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance,
      locale: 'en',
    })

    this.paymentElement = this.elements.create('payment', {
      wallets: {
        applePay: 'auto',
        googlePay: 'auto',
      },
    })

    this.paymentElement.mount(this.$refs.paymentElement)

    this.paymentElement.on('change', (event) => {
      this.isFormComplete = event.complete
    })
  },
  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.unmount()
    }
    this.$root.$emit('payment-modal:close')
    this.getMe()
  },
  methods: {
    ...mapActions('payment', ['buyPackage', 'fetchCurrentPlan']),
    ...mapActions('user', ['getMe']),
    ...mapMutations('payment', ['SET_REDIRECT_TO_LAST_ROUTE_MODAL']),

    async fetchClientSecret() {
      this.loadingSecret = true
      try {
        const { data: { clientSecret } } = await this.buyPackage({
          packageId: this.packageData._id,
          paymentMethod: 'card',
        })
        this.clientSecret = clientSecret
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load payment details', life: 3000 })
      } finally {
        this.loadingSecret = false
      }
    },

    async handleContinueFlow() {
      await this.fetchCurrentPlan()
      const comeBackPath = localStorage.getItem('comeBackPath')

      if (comeBackPath) {
        localStorage.removeItem('comeBackPath')
        try {
          await this.$router.push(comeBackPath)
        } catch (e) {
          console.warn('Navigation error', e)
        }
      }

      this.$emit('close')
    },

    async handleSubmit() {
      // Защита от отправки пустой формы (например, через Enter)
      if (!this.stripe || !this.elements || !this.isFormComplete) return

      this.submitLoading = true
      let error = null

      if (this.isTrial) {
        const result = await this.stripe.confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: 'if_required',
        })
        error = result.error
      } else {
        const result = await this.stripe.confirmPayment({
          elements: this.elements,
          redirect: 'if_required',
        })
        error = result.error
      }

      if (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error Message',
          detail: error.message,
          life: 3000,
        })
      } else {
        this.success = true
        await this.fetchCurrentPlan()

        // const comeBackPath = localStorage.getItem('comeBackPath')
        // if (comeBackPath) {
        //   this.SET_REDIRECT_TO_LAST_ROUTE_MODAL(true)
        // }
      }

      this.submitLoading = false
    },

    async handleClose() {
      await this.fetchCurrentPlan()
      this.$emit('close')
    }
  },
}
</script>

<style>
@media (max-width: 480px) {
  .modal-backdrop:has(.stripe-modal) {
    align-items: flex-end;
  }
}
</style>

<style scoped>
.stripe-modal {
  background: var(--Background-Primary);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 500px;
  width: 100%;
  max-height: 90dvh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.stripe-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
}

.stripe-modal__trial-notice {
  background-color: rgba(25, 75, 251, 0.05);
  border-left: 4px solid var(--Brand-Main, #194BFB);
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 4px;
  font-size: 14px;
  color: var(--Basic-900, #0E0B3D);
  font-weight: 500;
  line-height: 20px;
}

.stripe-modal__header-logo {
  width: 90px;
  flex-shrink: 0;
}

.stripe-modal__close-button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stripe-modal__close-button:hover {
  background: var(--light-grey-e-f);
}

.stripe-modal__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 16px;
}

.stripe-modal__success {
  padding: 60px 16px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.stripe-modal__success-title {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
}

.stripe-modal__success-description {
  margin: 0 0 32px 0;
  color: var(--Shadow-Mountain);
  font-size: 16px;
}

.stripe-modal__continue-button {
  height: 34px;
}

.stripe-modal__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.stripe-modal__scroll-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
  padding-bottom: 20px;
}

.stripe-modal__card-wrapper {
  margin-bottom: 24px;
}

.stripe-modal__form {
  width: 100%;
}

.stripe-modal__payment-element {
  margin-bottom: 24px;
  width: 100%;
}

.stripe-modal__footer {
  background: var(--Background-Primary);
  border-top: 1px solid var(--Border-Primary);
  padding: 16px 24px;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.stripe-modal__pay-button {
  width: 100%;
  background: var(--Brand-Main);
  color: var(--white);
  border: none;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  font-family: system-ui, sans-serif;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stripe-modal__pay-button:not(:disabled):hover {
  background: #5a52ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 91, 255, 0.3);
}

.stripe-modal__pay-button:disabled,
.stripe-modal__pay-button--loading {
  background: var(--blue-gray);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.stripe-modal__pay-button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.stripe-modal__pay-button-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.stripe-modal__pay-button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@media (max-width: 480px) {
  .modal-backdrop:has(.stripe-modal) {
    align-items: flex-end;
  }

  .stripe-modal {
    border-radius: 24px 24px 0 0;
  }

  .stripe-modal__header {
    padding: 16px;
  }

  .stripe-modal__scroll-content {
    padding: 0 16px 16px 16px;
  }

  .stripe-modal__footer {
    padding: 12px 16px;
  }

  .stripe-modal__payment-element {
    margin-bottom: 20px;
  }

  .stripe-modal__pay-button {
    padding: 12px 16px;
    font-size: 14px;
    min-height: 48px;
  }

  .stripe-modal__success-title {
    font-size: 20px;
  }

  .stripe-modal__success-description {
    font-size: 14px;
  }
}
</style>
